import {
  Form,
  FormInstance,
  Input,
  InputNumber,
  Row,
  Select,
  Switch,
} from "antd";
import { useTranslation } from "react-i18next";
import { DynamicModel } from "../../api/models";
import { useAttributesValidationLogic } from "./useAttributesValidationLogic";

interface BeaconConfigFormBulkProps {
  form: FormInstance<any>;
  onValuesChange?: (changedValues: any, allValues: any) => void;
}

export function BeaconConfigFormBulk({
  form,
  onValuesChange,
}: BeaconConfigFormBulkProps) {
  const { t, i18n } = useTranslation();
  const attributesValidationLogic = useAttributesValidationLogic();

  const inputStyle = {
    width: "225px",
  };

  const formatLabel = (body: React.ReactNode, style?: React.CSSProperties) => {
    return (
      <div
        style={{
          width: 225,
          whiteSpace: "normal",
          wordWrap: "break-word",
          ...style,
        }}
      >
        {body}
      </div>
    );
  };

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        requiredMark="optional"
        initialValues={{
          xAxis: true,
          yAxis: true,
          zAxis: true,
        }}
        onValuesChange={async (changedValues, allValues) => {
          onValuesChange?.(changedValues, allValues);
        }}
      >
        <h3
          style={{
            borderBottom: "1px solid #F0F0F0",
            margin: "0 0 24px 0 ",
            flex: "1",
          }}
        >
          {t("beacon:configure-general")}
        </h3>
        <div
          style={{
            display: "flex",
            justifyContent: "space-evenly",
            alignContent: "center",
          }}
        >
          <Form.Item
            name="activityInterval"
            label={formatLabel(t("beacon:configure-activity-interval"))}
            rules={[
              { required: true, message: t("required") },
              {
                pattern: attributesValidationLogic["activityInterval"].pattern,
                message: attributesValidationLogic["activityInterval"].message,
              },
            ]}
          >
            <InputNumber style={inputStyle} />
          </Form.Item>
          <Form.Item
            name="inactivityInterval"
            label={formatLabel(t("beacon:configure-inactivity-interval"))}
            rules={[
              { required: true, message: t("required") },
              {
                pattern:
                  attributesValidationLogic["inactivityInterval"].pattern,
                message:
                  attributesValidationLogic["inactivityInterval"].message,
              },
            ]}
          >
            <InputNumber style={inputStyle} />
          </Form.Item>
          <Form.Item
            name="heartbeat"
            label={formatLabel(t("beacon:configure-heartbeat-interval"))}
            rules={[
              { required: true, message: t("required") },
              {
                pattern: attributesValidationLogic["heartbeat"].pattern,
                message: attributesValidationLogic["heartbeat"].message,
              },
            ]}
          >
            <InputNumber style={inputStyle} />
          </Form.Item>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-evenly",
            alignContent: "center",
          }}
        >
          <Form.Item
            name="ignoreMotion"
            label={formatLabel(t("beacon:configure-ignore-motion"))}
            rules={[
              { required: true, message: t("required") },
              {
                pattern: attributesValidationLogic["ignoreMotion"].pattern,
                message: attributesValidationLogic["ignoreMotion"].message,
              },
            ]}
          >
            <InputNumber style={inputStyle} />
          </Form.Item>
        </div>
        <h3
          style={{ borderBottom: "1px solid #F0F0F0", margin: "0 0 24px 0 " }}
        >
          {t("beacon:configure-accelerometer")}
        </h3>
        <div
          style={{
            display: "flex",
            justifyContent: "space-evenly",
            alignContent: "center",
          }}
        >
          <Form.Item
            name="accelerometer.duration"
            label={formatLabel(t("beacon:configure-accelerometer-duration"))}
            rules={[
              { required: true, message: t("required") },
              {
                pattern:
                  attributesValidationLogic["accelerometer.duration"].pattern,
                message:
                  attributesValidationLogic["accelerometer.duration"].message,
              },
            ]}
          >
            <InputNumber style={inputStyle} />
          </Form.Item>
          <Form.Item
            name="accelerometer.threshold"
            label={formatLabel(t("beacon:configure-accelerometer-threshold"))}
            rules={[
              { required: true, message: t("required") },
              {
                pattern:
                  attributesValidationLogic["accelerometer.threshold"].pattern,
                message:
                  attributesValidationLogic["accelerometer.threshold"].message,
              },
            ]}
          >
            <InputNumber style={inputStyle} />
          </Form.Item>
        </div>

        <h4 style={{ textAlign: "center", marginBottom: "16px" }}>
          {t("beacon:configure-accelerometer-axis")}
        </h4>
        <Row gutter={24} style={{ display: "flex", justifyContent: "center" }}>
          <div style={{ textAlign: "center", margin: "10px" }}>
            <Form.Item name="accelerometer.x" valuePropName="checked">
              <Switch
                checkedChildren="Enabled"
                unCheckedChildren="Disabled"
                style={{ width: "90px" }}
              />
            </Form.Item>
            <div>{t("beacon:configure-accelerometer-x")}</div>
          </div>
          <div style={{ textAlign: "center", margin: "10px" }}>
            <Form.Item name="accelerometer.y" valuePropName="checked">
              <Switch
                checkedChildren="Enabled"
                unCheckedChildren="Disabled"
                style={{ width: "90px" }}
              />
            </Form.Item>
            <div>{t("beacon:configure-accelerometer-y")}</div>
          </div>
          <div style={{ textAlign: "center", margin: "10px" }}>
            <Form.Item name="accelerometer.z" valuePropName="checked">
              <Switch
                checkedChildren="Enabled"
                unCheckedChildren="Disabled"
                style={{ width: "90px" }}
              />
            </Form.Item>
            <div>{t("beacon:configure-accelerometer-z")}</div>
          </div>
        </Row>
        <h3
          style={{ borderBottom: "1px solid #F0F0F0", margin: "0 0 24px 0 " }}
        >
          {t("beacon:configure-positioning")}
        </h3>

        <div
          style={{
            display: "flex",
            justifyContent: "space-evenly",
            alignContent: "center",
          }}
        >
          <Form.Item
            name="positioning.heartbeat"
            label={formatLabel(t("beacon:configure-positioning-heartbeat"))}
            rules={[
              { required: true, message: t("required") },
              {
                pattern:
                  attributesValidationLogic["positioning.heartbeat"].pattern,
                message:
                  attributesValidationLogic["positioning.heartbeat"].message,
              },
            ]}
          >
            <InputNumber style={inputStyle} />
          </Form.Item>
          <Form.Item
            name="positioning.ttffTimeout"
            label={formatLabel(t("beacon:configure-positioning-ttffTimeout"), {
              marginBottom: i18n.language === "en" ? "20px" : "0px",
            })}
            rules={[
              { required: true, message: t("required") },
              {
                pattern:
                  attributesValidationLogic["positioning.ttffTimeout"].pattern,
                message:
                  attributesValidationLogic["positioning.ttffTimeout"].message,
              },
            ]}
          >
            <InputNumber style={inputStyle} />
          </Form.Item>
          <Form.Item
            name="positioning.ttffRetries"
            label={formatLabel(t("beacon:configure-positioning-ttffRetries"), {
              marginBottom: i18n.language === "en" ? "20px" : "0px",
            })}
            rules={[
              { required: true, message: t("required") },
              {
                pattern:
                  attributesValidationLogic["positioning.ttffRetries"].pattern,
                message:
                  attributesValidationLogic["positioning.ttffRetries"].message,
              },
            ]}
          >
            <InputNumber style={inputStyle} />
          </Form.Item>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-evenly",
            alignContent: "center",
          }}
        >
          <Form.Item
            name="positioning.fixQualityTimeout"
            label={formatLabel(
              t("beacon:configure-positioning-fixQualityTimeout")
            )}
            rules={[
              { required: true, message: t("required") },
              {
                pattern:
                  attributesValidationLogic["positioning.fixQualityTimeout"]
                    .pattern,
                message:
                  attributesValidationLogic["positioning.fixQualityTimeout"]
                    .message,
              },
            ]}
          >
            <InputNumber style={inputStyle} />
          </Form.Item>
          <Form.Item
            name="positioning.dynamicModel"
            label={formatLabel(t("beacon:configure-positioning-dynamicModel"), {
              marginBottom: "20px",
            })}
            rules={[{ required: true }]}
          >
            <Select style={{ width: "175px" }}>
              <Select.Option value={0}>{DynamicModel[0]}</Select.Option>
              <Select.Option value={2}>{DynamicModel[2]}</Select.Option>
              <Select.Option value={3}>{DynamicModel[3]}</Select.Option>
              <Select.Option value={4}>{DynamicModel[4]}</Select.Option>
              <Select.Option value={11}>{DynamicModel[11]}</Select.Option>
            </Select>
          </Form.Item>
        </div>
        <h3
          style={{ borderBottom: "1px solid #F0F0F0", margin: "0 0 24px 0 " }}
        >
          {t("beacon:configure-rtk")}
        </h3>
        <div
          style={{
            display: "flex",
            justifyContent: "space-evenly",
            alignContent: "center",
          }}
        >
          <Form.Item
            name="rtk.credentials.host"
            label={t("beacon:configure-rtk-host")}
            rules={[{ required: true, message: t("required") }]}
          >
            <Input style={inputStyle} />
          </Form.Item>
          <Form.Item
            name="rtk.credentials.port"
            label={t("beacon:configure-rtk-port")}
            rules={[
              { required: true, message: t("required") },
              {
                pattern:
                  attributesValidationLogic["rtk.credentials.port"].pattern,
                message:
                  attributesValidationLogic["rtk.credentials.port"].message,
              },
            ]}
          >
            <Input style={inputStyle} />
          </Form.Item>
          <Form.Item
            name="rtk.credentials.mountpoint"
            label={t("beacon:configure-rtk-mountpoint")}
            rules={[{ required: true, message: t("required") }]}
          >
            <Input style={inputStyle} />
          </Form.Item>
        </div>
        <div
          style={{
            display: "flex",
            justifyContent: "space-evenly",
            alignContent: "center",
          }}
        >
          <Form.Item
            name="rtk.credentials.user"
            label={t("beacon:configure-rtk-username")}
            rules={[{ required: true, message: t("required") }]}
          >
            <Input style={{ width: "300px" }} />
          </Form.Item>
          <Form.Item
            name="rtk.credentials.password"
            label={t("beacon:configure-rtk-password")}
            rules={[{ required: true, message: t("required") }]}
          >
            <Input style={{ width: "300px" }} />
          </Form.Item>
        </div>
      </Form>
    </>
  );
}
